import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Top-level error boundary. Prevents a render error anywhere in the tree from
 * showing a blank white page. Provides a recovery action (reload).
 */
export class GlobalErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (typeof console !== "undefined") {
      console.error("[GlobalErrorBoundary]", error, info);
    }
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-4">
        <div className="max-w-lg w-full border border-destructive/60 bg-destructive/5 p-6">
          <div className="text-[10px] uppercase tracking-[0.22em] text-destructive mb-2">
            ! runtime exception
          </div>
          <h1 className="text-xl font-bold mb-2">Something broke.</h1>
          <p className="text-[12px] text-muted-foreground mb-4">
            A non-fatal error was caught and isolated. Reload to recover the
            session.
          </p>
          {this.state.error?.message && (
            <pre className="text-[11px] text-destructive/80 bg-background border border-border p-2 mb-4 overflow-x-auto">
              {this.state.error.message}
            </pre>
          )}
          <button
            type="button"
            onClick={this.handleReload}
            className="px-3 py-2 text-[11px] uppercase tracking-[0.2em] bg-primary text-primary-foreground hover:bg-primary/90"
          >
            [ ./reload ]
          </button>
        </div>
      </div>
    );
  }
}
